import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate, RESERVATION_TYPE_LABELS, STATUS_BADGE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { Ship, Calendar, ClipboardCheck, AlertTriangle, BookOpen, Wrench, ArrowUpRight, Activity, ShieldCheck, Camera } from "lucide-react";
import WeatherWidget from "@/components/WeatherWidget";
import UsageBalanceWidget from "@/components/UsageBalanceWidget";
import MaintenanceWriteupCard from "@/components/MaintenanceWriteupCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import JournalDialog from "@/components/JournalDialog";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, sub, tone, testid, onClick }) {
  const toneMap = {
    blue: "text-[#4DA6FF] bg-[#007AFF]/10 border-[#007AFF]/30",
    amber: "text-[#FBBF24] bg-[#F59E0B]/10 border-[#F59E0B]/30",
    emerald: "text-[#34D399] bg-[#10B981]/10 border-[#10B981]/30",
    red: "text-[#F87171] bg-[#EF4444]/10 border-[#EF4444]/30",
    slate: "text-[#F8FAFC] bg-[#131C31] border-[#1E293B]",
  };
  const Element = onClick ? "button" : "div";
  return (
    <Element
      className={`ledger-card p-5 ${onClick ? "text-left hover:border-[#34D399]/40 transition-colors cursor-pointer w-full" : ""}`}
      data-testid={testid}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-md border flex items-center justify-center ${toneMap[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{label}</div>
      </div>
      <div className="font-display text-3xl font-bold tracking-tight stat-number">{value}</div>
      {sub && <div className="text-xs text-[#94A3B8] mt-1">{sub}</div>}
    </Element>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMissing, setShowMissing] = useState(false);
  const [missing, setMissing] = useState([]);
  const [missingLoading, setMissingLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [allReservations, setAllReservations] = useState([]);
  const [logTrip, setLogTrip] = useState(null); // { reservation_id, asset_id }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/summary");
        setData(data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const openMissingDialog = async () => {
    setShowMissing(true);
    if (missing.length > 0) return; // already loaded
    setMissingLoading(true);
    try {
      const [m, a, r] = await Promise.all([
        api.get("/reports/missing-logs"),
        api.get("/assets"),
        api.get("/reservations"),
      ]);
      setMissing(m.data?.missing || []);
      setAssets(a.data);
      setAllReservations(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load missing logs");
    } finally {
      setMissingLoading(false);
    }
  };

  const handleStartLog = (row) => {
    // Sequence the transitions: close the missing-logs dialog *first*,
    // then open the JournalDialog one tick later. Two Radix dialogs
    // closing/opening in the same React batch can leave the body
    // scroll-lock stuck on iOS Safari PWA — the page appears "frozen"
    // until you tap somewhere else. Adding a 220ms delay (≈ Radix's
    // 200ms fade-out animation) sidesteps the issue entirely.
    setShowMissing(false);
    setTimeout(() => setLogTrip({ reservation_id: row.id, asset_id: row.asset_id }), 220);
  };

  const handleLogSaved = async () => {
    setLogTrip(null);
    // refresh both summary + missing list
    try {
      const [s, m] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/reports/missing-logs"),
      ]);
      setData(s.data);
      setMissing(m.data?.missing || []);
    } catch {}
  };

  // Safety net: when both dialogs are closed, defensively restore body
  // scroll/pointer-events. Radix occasionally leaves these set after rapid
  // dialog-to-dialog transitions on iOS PWA (the "frozen screen" symptom).
  useEffect(() => {
    if (!showMissing && !logTrip) {
      // Schedule on next frame so we don't fight Radix's own teardown.
      const tid = setTimeout(() => {
        const body = document.body;
        if (body.style.pointerEvents === "none") body.style.pointerEvents = "";
        if (body.style.overflow === "hidden" && !document.querySelector('[role="dialog"][data-state="open"]')) {
          body.style.overflow = "";
        }
      }, 50);
      return () => clearTimeout(tid);
    }
  }, [showMissing, logTrip]);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Hero strip */}
      <div className="ledger-card p-6 relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] tracking-widest text-[#4DA6FF]">{t("dashboard.welcome")}</div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">
              {t("dashboard.welcomeBack")} {user?.name?.split(" ")[0]}.
            </h2>
            <p className="text-sm text-[#94A3B8] mt-1">
              {loading
                ? t("dashboard.statusLoading")
                : t("dashboard.statusLine", { assets: data?.total_assets ?? 0, logs: data?.total_logs ?? 0, pending: data?.pending_approvals?.length ?? 0 })}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => navigate("/app/calendar")} data-testid="dashboard-new-reservation-btn"
              className="px-4 py-2 rounded-md bg-[#007AFF] hover:bg-[#0056B3] text-white text-sm font-medium transition-colors flex items-center gap-2"
            >
              <Calendar className="w-4 h-4" /> {t("dashboard.newReservation")}
            </button>
            <button
              onClick={() => navigate("/app/journal")} data-testid="dashboard-new-log-btn"
              className="px-4 py-2 rounded-md bg-[#0B1221] hover:bg-[#131C31] border border-[#1E293B] text-sm transition-colors flex items-center gap-2"
            >
              <BookOpen className="w-4 h-4" /> {t("dashboard.logTrip")}
            </button>
            <button
              onClick={() => navigate("/app/invoices?snap=1")} data-testid="dashboard-snap-receipt-btn"
              className="px-4 py-2 rounded-md bg-[#0B1221] hover:bg-[#131C31] border border-[#1E293B] hover:border-[#4DA6FF] text-sm transition-colors flex items-center gap-2"
            >
              <Camera className="w-4 h-4 text-[#4DA6FF]" /> {t("dashboard.snapReceipt")}
            </button>
          </div>
        </div>
      </div>

      {/* Owner usage balance (Rok vs Borut) — prominent, top of dashboard */}
      <UsageBalanceWidget />

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-stats">
        <StatCard icon={Ship} tone="blue" label={t("dashboard.fleet")} value={data?.total_assets ?? 0} sub={t("dashboard.fleetSub")} testid="stat-assets" />
        <StatCard icon={ClipboardCheck} tone="amber" label={t("dashboard.awaitingVote")} value={data?.pending_approvals?.length ?? 0} sub={t("dashboard.awaitingVoteSub")} testid="stat-pending" />
        <StatCard icon={AlertTriangle} tone="red" label={t("dashboard.openTickets")} value={data?.open_tickets_count ?? 0} sub={t("dashboard.openTicketsSub")} testid="stat-tickets" />
        <StatCard icon={Activity} tone="emerald" label={t("dashboard.missingLogs")} value={data?.missing_logs_count ?? 0} sub={(data?.missing_logs_count ?? 0) > 0 ? t("dashboard.missingLogs.clickHint") : t("dashboard.missingLogsSub")} testid="stat-missing" onClick={openMissingDialog} />
      </div>

      {/* Weather quick view */}
      <WeatherWidget />

      {/* Maintenance write-up reminder (planned jobs that have ended) */}
      <MaintenanceWriteupCard />

      {/* Split grid: upcoming + pending */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Upcoming */}
        <div className="ledger-card p-5 lg:col-span-2" data-testid="dashboard-upcoming">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("dashboard.upcomingLabel")}</div>
              <h3 className="font-display text-lg font-bold tracking-tight mt-0.5">{t("dashboard.upcomingTitle")}</h3>
            </div>
            <button onClick={() => navigate("/app/calendar")} className="text-xs text-[#4DA6FF] hover:underline flex items-center gap-1">
              {t("dashboard.viewCalendar")} <ArrowUpRight className="w-3 h-3" />
            </button>
          </div>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-12 bg-[#131C31] animate-pulse rounded-md" />)}
            </div>
          ) : (data?.upcoming?.length ?? 0) === 0 ? (
            <EmptyState icon={Calendar} message={t("dashboard.noUpcoming")} />
          ) : (
            <div className="divide-y divide-[#1E293B]">
              {data.upcoming.map((r) => (
                <div key={r.id} className="flex items-center justify-between py-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`badge-pill ${STATUS_BADGE[r.status] || "badge-neutral"}`}>{t(`status.${r.status}`)}</span>
                      {r.is_protected_private_term && <span className="badge-pill badge-protected">{t("status.protected")}</span>}
                    </div>
                    <div className="font-medium text-sm truncate">{t(`reservation.${r.reservation_type}`)} · {r.client_name || t("reservation.private")}</div>
                    <div className="font-mono text-[11px] text-[#94A3B8]">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pending approvals */}
        <div className="ledger-card p-5" data-testid="dashboard-pending">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] tracking-widest text-[#F59E0B]">{t("dashboard.dualApproval")}</div>
              <h3 className="font-display text-lg font-bold tracking-tight mt-0.5">{t("dashboard.awaitingDirectors")}</h3>
            </div>
            <ShieldCheck className="w-4 h-4 text-[#FBBF24]" />
          </div>
          {(data?.pending_approvals?.length ?? 0) === 0 ? (
            <EmptyState icon={ShieldCheck} message={t("dashboard.noPending")} />
          ) : (
            <div className="space-y-3">
              {data.pending_approvals.slice(0, 5).map((r) => (
                <button
                  key={r.id} onClick={() => navigate("/app/commercial")}
                  className="w-full text-left p-3 rounded-md bg-[#050A14] border border-[#1E293B] hover:border-[#F59E0B]/50 transition-colors"
                  data-testid={`pending-${r.id}`}
                >
                  <div className="font-medium text-sm truncate">{t(`reservation.${r.reservation_type}`)}</div>
                  <div className="font-mono text-[11px] text-[#94A3B8]">
                    {fmtDate(r.start_date)} → {fmtDate(r.end_date)} · {r.approvals?.length || 0}/2 {t("dashboard.votes")}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent logs */}
      <div className="ledger-card p-5" data-testid="dashboard-recent-logs">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("dashboard.recentActivity")}</div>
            <h3 className="font-display text-lg font-bold tracking-tight mt-0.5">{t("dashboard.captainsJournal")}</h3>
          </div>
          <button onClick={() => navigate("/app/journal")} className="text-xs text-[#4DA6FF] hover:underline flex items-center gap-1">
            {t("dashboard.openJournal")} <ArrowUpRight className="w-3 h-3" />
          </button>
        </div>
        {(data?.recent_logs?.length ?? 0) === 0 ? (
          <EmptyState icon={BookOpen} message={t("dashboard.noLogs")} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.recent_logs.map((l) => (
              <div key={l.id} className="p-4 rounded-md bg-[#050A14] border border-[#1E293B]">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`badge-pill ${l.log_type === "fishing_log" ? "badge-fishing" : "badge-neutral"}`}>
                    {t(`journal.logType.${l.log_type}`)}
                  </span>
                  <span className="font-mono text-[10px] text-[#94A3B8]">{fmtDate(l.log_date)}</span>
                </div>
                <div className="font-medium text-sm mb-1 truncate">{l.place_name || l.route || t("journal.untitled")}</div>
                <div className="text-xs text-[#94A3B8] line-clamp-2">{l.incidents || l.dos_note || l.guest_experience_note || "—"}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Missing logs dialog */}
      {showMissing && (
        <Dialog open onOpenChange={(o) => !o && setShowMissing(false)}>
          <DialogContent className="bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] max-w-2xl" data-testid="missing-logs-dialog">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#34D399]" />
                {t("dashboard.missingLogs.title")}
              </DialogTitle>
              <DialogDescription className="text-sm text-[#94A3B8]">{t("dashboard.missingLogs.intro")}</DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto space-y-2 mt-2" data-testid="missing-logs-list">
              {missingLoading ? (
                <div className="ledger-card !bg-[#050A14] p-6 text-center text-[#94A3B8]">
                  <span className="animate-pulse">…</span>
                </div>
              ) : missing.length === 0 ? (
                <div className="ledger-card !bg-[#050A14] p-6 text-center text-[#94A3B8]" data-testid="missing-logs-empty">
                  {t("dashboard.missingLogs.empty")}
                </div>
              ) : (
                missing.map((row) => {
                  const asset = assets.find((a) => a.id === row.asset_id);
                  const daysAgo = Math.floor((new Date() - new Date(row.end_date)) / 86400000);
                  return (
                    <button
                      key={row.id}
                      onClick={() => handleStartLog(row)}
                      data-testid={`missing-log-row-${row.id}`}
                      className="w-full text-left p-3 rounded-md bg-[#050A14] border border-[#1E293B] hover:border-[#34D399]/50 transition-colors flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm break-words">
                          {asset?.name || "—"} · {t(`reservation.${row.reservation_type}`)}
                        </div>
                        <div className="font-mono text-[11px] text-[#94A3B8] mt-0.5">
                          {fmtDate(row.start_date)} → {fmtDate(row.end_date)}
                          {daysAgo >= 0 && <> · {t("dashboard.missingLogs.daysAgo", { n: daysAgo })}</>}
                        </div>
                      </div>
                      <span className="self-start sm:self-auto px-3 py-1.5 rounded-md bg-[#34D399]/10 border border-[#34D399]/30 text-[#34D399] text-xs whitespace-nowrap flex items-center gap-1 flex-shrink-0">
                        <BookOpen className="w-3 h-3" /> {t("dashboard.missingLogs.logBtn")}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Inline log-trip dialog (pre-filled from a missing-logs row) */}
      {logTrip && (
        <JournalDialog
          open
          onOpenChange={(o) => !o && setLogTrip(null)}
          assets={assets}
          reservations={allReservations}
          defaultReservationId={logTrip.reservation_id}
          defaultAssetId={logTrip.asset_id}
          onSaved={handleLogSaved}
        />
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, message }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-[#94A3B8]">
      <Icon className="w-8 h-8 mb-2 opacity-60" />
      <div className="text-xs">{message}</div>
    </div>
  );
}
